import React, { useState } from 'react';
import { Modal, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Textarea } from '../ui/form';
import { useCreateExternalOrder } from '../../hooks/use-external-orders';
import { ExternalOrderSubmissionResponse } from '../../services/external-order-service';
import { useDivisionContext } from '../../contexts/division-context';

interface ExternalOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  jsonData: string;
}

export function ExternalOrderModal({ isOpen, onClose }: ExternalOrderModalProps) {
  const [formData, setFormData] = useState<FormData>({
    jsonData: ''
  });
  const [errors, setErrors] = useState<{ jsonData?: string }>({});

  const { selectedDivisionId } = useDivisionContext();
  const createExternalOrderMutation = useCreateExternalOrder();

  const validateForm = (): boolean => {
    const newErrors: { jsonData?: string } = {};

    if (!formData.jsonData.trim()) {
      newErrors.jsonData = 'JSON data is required';
    } else {
      try {
        JSON.parse(formData.jsonData);
      } catch (error) {
        newErrors.jsonData = 'Invalid JSON format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    // Submit the order with raw JSON data and the current division ID
    createExternalOrderMutation.mutate(
      { orderData: formData.jsonData, divisionId: selectedDivisionId || undefined },
      {
        onSuccess: (response: ExternalOrderSubmissionResponse) => {
          // Show success message with order details
          alert(`Order submitted successfully!\nOrder ID: ${response.orderId}\nStatus: ${response.status}\nTracking URL: ${response.trackingUrl || 'N/A'}`);
          handleClose();
        },
        onError: (error: any) => {
          // Extract full error details from API response
          let errorMessage = 'Failed to submit order';
          
          if (error.apiError) {
            // Use structured API error if available
            errorMessage = error.apiError.message;
            
            // Add error code if available
            if (error.apiError.errorCode) {
              errorMessage = `[${error.apiError.errorCode}] ${errorMessage}`;
            }
            
            // Add additional details if available
            if (error.apiError.details) {
              const details = error.apiError.details;
              if (details.orderNumber) {
                errorMessage += `\n\nOrder Number: ${details.orderNumber}`;
              }
              if (details.suggestion) {
                errorMessage += `\n\nSuggestion: ${details.suggestion}`;
              }
              // Add any other detail properties
              Object.keys(details).forEach(key => {
                if (key !== 'orderNumber' && key !== 'suggestion') {
                  errorMessage += `\n${key}: ${details[key]}`;
                }
              });
            }
          } else {
            // Fallback to standard error message
            errorMessage += `: ${error.message || 'Unknown error'}`;
          }
          
          setErrors({ jsonData: errorMessage });
        }
      }
    );
  };

  const handleClose = () => {
    setFormData({ jsonData: '' });
    setErrors({});
    onClose();
  };

  // Sample JSON for the placeholder
  const sampleJson = JSON.stringify({
    "ExternalShopperId": "user@company.com",
    "OrderDetails": {
      "Title": "Office Supplies Order",
      "Description": "Monthly office supplies requisition",
      "Currency": "USD",
      "ShipTo": {
        "Address": "123 Business Ave",
        "City": "Business City",
        "State": "BC",
        "PostalCode": "12345",
        "Country": "US"
      },
      "BillTo": {
        "Address": "123 Business Ave",
        "City": "Business City", 
        "State": "BC",
        "PostalCode": "12345",
        "Country": "US"
      }
    },
    "LineItems": [
      {
        "ItemId": "PENS001",
        "Description": "Blue Ballpoint Pens",
        "Quantity": 50,
        "UnitPrice": 0.99,
        "TotalPrice": 49.50,
        "Category": "Writing Supplies"
      }
    ],
    "Metadata": {
      "SourceSystem": "ProcurementApp",
      "SourceSystemId": "ORDER-2024-001",
      "SubmittedBy": "user@company.com",
      "Tags": ["office-supplies", "monthly-order"],
      "RemoteReferenceNumber": "EXT-ORDER-" + Date.now()
    }
  }, null, 2);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Submit External Order"
      size="lg"
    >
      <div className="space-y-6">
        <div>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
            Submit an order using JSON format. The order will be processed through the external orders API.
          </p>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">
              Order JSON Data <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={formData.jsonData}
              onChange={(e) => setFormData({ ...formData, jsonData: e.target.value })}
              placeholder={`Enter JSON order data, for example:\n\n${sampleJson}`}
              rows={20}
              className={`font-mono text-sm ${errors.jsonData ? 'border-red-300' : ''}`}
            />
            {errors.jsonData && (
              <p className="text-sm text-red-600 dark:text-red-400">{errors.jsonData}</p>
            )}
          </div>

          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Required Fields:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <code>ExternalShopperId</code> - Email of the person submitting the order</li>
              <li>• <code>OrderDetails.Title</code> - Order title/name</li>
              <li>• <code>LineItems</code> - Array of items being ordered</li>
              <li>• <code>Metadata.RemoteReferenceNumber</code> - External reference number</li>
            </ul>
          </div>
        </div>
      </div>

      <ModalFooter>
        <Button
          variant="outline"
          onClick={handleClose}
          disabled={createExternalOrderMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={createExternalOrderMutation.isPending || !formData.jsonData.trim()}
        >
          {createExternalOrderMutation.isPending ? 'Submitting...' : 'Submit Order'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}